import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import {
  ListItem,
  ListItemIcon,
  Checkbox,
  CircularProgress,
  IconButton
} from '@material-ui/core';
import { useIntl } from 'react-intl';
import { NoValue, DateRange } from '~/modules/common/components';
import {
  MilestoneIcon,
  OpenInDrawerIcon
} from '~/modules/common/components/Icons';
import { useDialogState } from '~/modules/common/hooks';
import { PROJECT_PERMISSION } from '~/modules/common/enums';
import { RemoveTaskAssignmentConfirmationDialog } from '~/modules/tasks/components/TaskDrawer/TaskResourceEstimates/components/RemoveTaskAssignmentDialog';
import { roundToDecimals } from '~/modules/resourcing/common/util/scheduleUtil';
import { useOnChangeHandlers } from '../hooks';
import { AssignmentInfo } from './AssignmentInfo';

const indentWidths = [16, 40, 56, 72, 88, 104, 120, 136, 152, 168, 184, 200];

const assignmentContainerId = containerId => `${containerId}_assignment`;

const useStyles = makeStyles(theme => ({
  root: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    paddingTop: 0,
    paddingBottom: 0,
    cursor: 'default',
    '&:hover': {
      background: 'none'
    }
  },
  taskRowContainer: {
    display: 'flex',
    flexWrap: 'nowrap',
    alignItems: 'center',
    overflow: 'hidden',
    width: '65%'
  },
  assignmentInfoContainer: {
    width: '35%'
  },
  indent: {
    flexGrow: 0,
    flexShrink: 0,
    alignSelf: 'stretch'
  },
  primary: {
    fontSize: theme.typography.body2.fontSize,
    fontWeight: theme.typography.fontWeightBold
  },
  dateRange: {
    color: theme.palette.text.secondary
  },
  milestoneIcon: {
    width: theme.spacing(2),
    height: theme.spacing(2),
    margin: theme.spacing(0, 0.5, 0, 0),
    verticalAlign: 'text-bottom'
  },
  taskDetailsContainer: {
    display: 'flex',
    padding: theme.spacing(1, 0),
    flexDirection: 'column',
    width: '100%'
  },
  checkArea: {
    minWidth: theme.spacing(4),
    width: theme.spacing(4)
  },
  circularProgress: {
    paddingLeft: theme.spacing(1)
  }
}));

const useCheckBoxStyles = makeStyles(theme => ({
  root: {
    '&.Mui-focusVisible': {
      outline: 'none',
      backgroundColor: theme.palette.grey[200]
    }
  }
}));

export const TaskAssignmentRow = ({
  task,
  resource,
  resourceEstimate,
  permittedActionUris,
  projectId,
  primaryRole,
  onTaskAssignmentUpdateSuccess,
  setActiveRequests,
  refetchQueriesOnRemoveTaskAssignment
}) => {
  const classes = useStyles();
  const checkBoxClasses = useCheckBoxStyles();
  const { formatMessage } = useIntl();

  const canViewCost = permittedActionUris.includes(
    PROJECT_PERMISSION.VIEW_COST_DATA
  );

  const canEditTask = permittedActionUris.includes(
    PROJECT_PERMISSION.EDIT_TASK
  );

  const canViewTask = permittedActionUris.includes(
    PROJECT_PERMISSION.VIEW_TASK
  );

  const {
    id: taskId,
    displayText,
    fullPath,
    startDate,
    endDate,
    isMilestone,
    assignedRole: taskRole,
    totalResourceEstimateHours,
    estimatedHours: taskEstimatedHours
  } = task;

  const { estimateId, projectRole, initialEstimatedHours } =
    resourceEstimate || {};

  const indentWidth =
    typeof indentWidths[fullPath.length - 1] === 'number'
      ? indentWidths[fullPath.length - 1]
      : 0;

  const [isAssignedTask, setIsAssignedTask] = useState(
    Boolean(estimateId || estimateId === null)
  );

  const defaultRoleForUnassignedTask = taskRole || primaryRole;

  const [roleDisplayText, setRoleDisplayText] = useState(
    isAssignedTask
      ? projectRole?.displayText
      : defaultRoleForUnassignedTask?.displayText
  );

  const [estimatedHours, setEstimatedHours] = useState(
    isAssignedTask
      ? initialEstimatedHours
      : taskEstimatedHours > totalResourceEstimateHours
      ? roundToDecimals(taskEstimatedHours - totalResourceEstimateHours)
      : undefined
  );

  const {
    open: removeTaskAssignmentDialogOpen,
    openDialog: openRemoveTaskAssignmentDialog,
    closeDialog: closeRemoveTaskAssignmentDialog
  } = useDialogState(false);

  const {
    loading,
    handleTaskClick,
    handleDrawerIconClick,
    onRemoveTaskAssignmentSuccess,
    handleKeyDown,
    autoFocus
  } = useOnChangeHandlers({
    defaultRole: defaultRoleForUnassignedTask,
    estimatedHours,
    isAssignedTask,
    taskId,
    userId: resource.id,
    setIsAssignedTask,
    setRoleDisplayText,
    setEstimatedHours,
    closeRemoveTaskAssignmentDialog,
    openRemoveTaskAssignmentDialog,
    onTaskAssignmentUpdateSuccess,
    canEditTask,
    setActiveRequests
  });

  const dateRangeClasses = useMemo(
    () => ({
      range: classes.dateRange
    }),
    [classes.dateRange]
  );

  const checkboxAriaAttributes = useMemo(
    () => ({
      'aria-label': displayText,
      'aria-describedby': assignmentContainerId(displayText)
    }),
    [displayText]
  );

  return (
    <>
      {removeTaskAssignmentDialogOpen && (
        <RemoveTaskAssignmentConfirmationDialog
          open={removeTaskAssignmentDialogOpen}
          onClose={closeRemoveTaskAssignmentDialog}
          onRemoveTaskAssignmentSuccess={onRemoveTaskAssignmentSuccess}
          task={task}
          user={resource}
          projectId={projectId}
          refetchQueries={refetchQueriesOnRemoveTaskAssignment}
          canViewCost={canViewCost}
        />
      )}
      <ListItem className={classes.root} disabled={loading}>
        <ListItemIcon className={classes.checkArea}>
          {loading ? (
            <span className={classes.circularProgress}>
              <CircularProgress size={20} />
            </span>
          ) : (
            <Checkbox
              tabIndex={0}
              classes={checkBoxClasses}
              onKeyDown={handleKeyDown}
              onChange={handleTaskClick}
              checked={isAssignedTask}
              color="primary"
              disableRipple
              disabled={!canEditTask}
              inputProps={checkboxAriaAttributes}
              autoFocus={autoFocus}
            />
          )}
        </ListItemIcon>
        <div className={classes.taskRowContainer}>
          <div
            className={classes.indent}
            style={useMemo(() => ({ width: indentWidth }), [indentWidth])}
          />
          <div className={classes.taskDetailsContainer}>
            <span className={classes.primary}>
              {isMilestone ? (
                <MilestoneIcon className={classes.milestoneIcon} />
              ) : null}
              {displayText || <NoValue />}
            </span>
            <DateRange
              start={startDate}
              end={endDate}
              classes={dateRangeClasses}
            />
          </div>
          {canViewTask && (
            <ListItemIcon>
              <IconButton
                edge="end"
                component={Link}
                to={`#/task/${taskId}`}
                onClick={handleDrawerIconClick}
                aria-label={formatMessage(
                  {
                    id: 'taskAssignmentDialog.openDrawer'
                  },
                  { taskName: displayText }
                )}
              >
                <OpenInDrawerIcon />
              </IconButton>
            </ListItemIcon>
          )}
        </div>
        <div
          className={classes.assignmentInfoContainer}
          id={assignmentContainerId(displayText)}
        >
          <AssignmentInfo
            roleDisplayText={roleDisplayText}
            estimatedHours={estimatedHours}
            isAssignedTask={isAssignedTask}
          />
        </div>
      </ListItem>
    </>
  );
};

TaskAssignmentRow.propTypes = {
  task: PropTypes.object,
  resource: PropTypes.object.isRequired,
  resourceEstimate: PropTypes.object,
  permittedActionUris: PropTypes.array,
  projectId: PropTypes.string.isRequired,
  primaryRole: PropTypes.object,
  onTaskAssignmentUpdateSuccess: PropTypes.func.isRequired,
  setActiveRequests: PropTypes.func,
  refetchQueriesOnRemoveTaskAssignment: PropTypes.array
};

export default TaskAssignmentRow;
